import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import * as React from "react";
import { RuntimeScheduleConfiguration } from "../../models/ClientConfiguration/RuntimeScheduleConfiguration";
import AddableList from "../common/AddableList";
import FieldError from "../common/FieldError";
import RuntimeScheduleTypeSelect from "./RuntimeScheduleTypeSelect";

interface RuntimeScheduleControlProps {
  runtimeSchedules: RuntimeScheduleConfiguration[];
  onChange: (runtimeSchedules: RuntimeScheduleConfiguration[]) => void;
  disabled?: boolean;
}

const toTimespan = (timeInput: string): string =>
  timeInput ? `${timeInput}:00` : "";

const RuntimeScheduleControl: React.FC<RuntimeScheduleControlProps> = ({
  runtimeSchedules,
  onChange,
  disabled = false,
}) => {
  const updateSchedule = (index: number, patch: Partial<RuntimeScheduleConfiguration>) => {
    onChange(runtimeSchedules.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const handleTimeChange = (index: number, field: "startLocalTime" | "endLocalTime", inputValue: string) => {
    const newTime = toTimespan(inputValue);
    const schedule = runtimeSchedules[index];

    if (field === "startLocalTime" && newTime && schedule.endLocalTime && newTime > schedule.endLocalTime) {
      updateSchedule(index, { startLocalTime: schedule.endLocalTime, endLocalTime: newTime });
    } else if (field === "endLocalTime" && newTime && schedule.startLocalTime && newTime < schedule.startLocalTime) {
      updateSchedule(index, { startLocalTime: newTime, endLocalTime: schedule.startLocalTime });
    } else {
      updateSchedule(index, { [field]: newTime });
    }
  };

  return (
    <AddableList
      onAddClick={() => onChange([...runtimeSchedules, { startLocalTime: "", endLocalTime: "", scheduleType: 0 }])}
      subheaderText="Runtime Schedules"
      disabled={disabled}
    >
      {runtimeSchedules.map((schedule, index) => (
        <div className="flex flex-col gap-1" key={index}>
          <div className="flex items-center gap-2">
            <Input
              className="h-8 w-[6.5rem] shrink-0 bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              type="time"
              value={schedule.startLocalTime}
              onChange={(e) => handleTimeChange(index, "startLocalTime", e.target.value)}
              disabled={disabled}
            />
            <span className="text-muted-foreground text-sm shrink-0">to</span>
            <Input
              className="h-8 w-[6.5rem] shrink-0 bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              type="time"
              value={schedule.endLocalTime}
              onChange={(e) => handleTimeChange(index, "endLocalTime", e.target.value)}
              disabled={disabled}
            />
            <RuntimeScheduleTypeSelect
              value={schedule.scheduleType}
              onChange={(type) => updateSchedule(index, { scheduleType: type })}
              disabled={disabled}
            />
            <Button
              aria-label="Delete schedule"
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-destructive shrink-0 ml-auto"
              onClick={() => onChange(runtimeSchedules.filter((_, i) => i !== index))}
              disabled={disabled}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
          <FieldError name={`runtimeSchedules.${index}.startLocalTime`} />
          <FieldError name={`runtimeSchedules.${index}.endLocalTime`} />
        </div>
      ))}
    </AddableList>
  );
};

export default RuntimeScheduleControl;
